import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  boardCalendarFeedPatchSchema,
  boardCalendarFeedSchema,
  connectedAppConsentBodySchema,
  connectedAppConsentResponseSchema,
  connectedAppGrantSchema,
  connectedAppTokenRevealSchema,
  emailCaptureSettingsPatchSchema,
  emailToBoardAddressViewSchema,
  memberConnectedAppGrantListSchema,
  type ApiError,
  type BoardCalendarFeed,
  type BoardCalendarFeedPatch,
  type ConnectedAppConsentBody,
  type ConnectedAppConsentResponse,
  type ConnectedAppGrant,
  type ConnectedAppTokenReveal,
  type EmailCaptureSettingsPatch,
  type EmailToBoardAddressView,
  type MemberConnectedAppGrant,
  type MemberConnectedAppGrantList,
} from '@molt/shared';
import { useCallback } from 'react';
import { del, get, patch, post } from './api';
import { queryKeys } from './query-keys';

export function getEmailToBoardAddress(
  boardId: string,
): Promise<EmailToBoardAddressView> {
  return get(
    `/boards/${encodeURIComponent(boardId)}/email-to-board`,
    emailToBoardAddressViewSchema,
  );
}

export function regenerateEmailToBoardAddress(
  boardId: string,
): Promise<EmailToBoardAddressView> {
  return post(
    `/boards/${encodeURIComponent(boardId)}/email-to-board/regenerate`,
    emailToBoardAddressViewSchema,
    {},
  );
}

export function updateEmailToBoardSettings(
  boardId: string,
  body: EmailCaptureSettingsPatch,
): Promise<EmailToBoardAddressView> {
  return patch(
    `/boards/${encodeURIComponent(boardId)}/email-to-board/settings`,
    emailToBoardAddressViewSchema,
    emailCaptureSettingsPatchSchema.parse(body),
  );
}

export function getBoardCalendarFeed(boardId: string): Promise<BoardCalendarFeed> {
  return get(
    `/boards/${encodeURIComponent(boardId)}/calendar-feed`,
    boardCalendarFeedSchema,
  );
}

export function generateBoardCalendarFeedToken(
  boardId: string,
): Promise<BoardCalendarFeed> {
  return post(
    `/boards/${encodeURIComponent(boardId)}/calendar-feed/token`,
    boardCalendarFeedSchema,
    {},
  );
}

export function updateBoardCalendarFeed(
  boardId: string,
  body: BoardCalendarFeedPatch,
): Promise<BoardCalendarFeed> {
  return patch(
    `/boards/${encodeURIComponent(boardId)}/calendar-feed`,
    boardCalendarFeedSchema,
    boardCalendarFeedPatchSchema.parse(body),
  );
}

/**
 * AT-7 — the member's list is the MEMBER copy of the grant schema
 * (`status`, `tokenHint`, `issuedBy`). `POST` and `DELETE` below keep the
 * shipped shapes; only this read widened, and it widened on a copy.
 */
export function getConnectedApps(): Promise<MemberConnectedAppGrantList> {
  return get('/me/connected-apps', memberConnectedAppGrantListSchema);
}

export function createConnectedAppGrant(
  body: ConnectedAppConsentBody,
): Promise<ConnectedAppConsentResponse> {
  return post(
    '/me/connected-apps',
    connectedAppConsentResponseSchema,
    connectedAppConsentBodySchema.parse(body),
  );
}

export function revokeConnectedAppGrant(id: string): Promise<ConnectedAppGrant> {
  return del(`/me/connected-apps/${encodeURIComponent(id)}`, connectedAppGrantSchema);
}

/**
 * AT-8 — reveal (first time) or regenerate (every later time) the token of one
 * of the caller's own grants. Body-less, so it sends `{}` like every other
 * body-less POST here. The response carries the cleartext token exactly once.
 */
export function revealConnectedAppToken(id: string): Promise<ConnectedAppTokenReveal> {
  return post(
    `/me/connected-apps/${encodeURIComponent(id)}/token`,
    connectedAppTokenRevealSchema,
    {},
  );
}

export function useEmailToBoardAddress(
  boardId: string,
  enabled: boolean,
): UseQueryResult<EmailToBoardAddressView, ApiError> {
  return useQuery({
    queryKey: queryKeys.boards.emailToBoard(boardId),
    queryFn: () => getEmailToBoardAddress(boardId),
    enabled,
  });
}

export function useRegenerateEmailToBoardAddress(
  boardId: string,
): UseMutationResult<EmailToBoardAddressView, ApiError, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => regenerateEmailToBoardAddress(boardId),
    onSuccess: (view) => {
      queryClient.setQueryData(queryKeys.boards.emailToBoard(boardId), view);
    },
  });
}

export function useUpdateEmailToBoardSettings(
  boardId: string,
): UseMutationResult<EmailToBoardAddressView, ApiError, EmailCaptureSettingsPatch> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => updateEmailToBoardSettings(boardId, body),
    onSuccess: (view) => {
      queryClient.setQueryData(queryKeys.boards.emailToBoard(boardId), view);
    },
  });
}

export function useBoardCalendarFeed(
  boardId: string,
  enabled: boolean,
): UseQueryResult<BoardCalendarFeed, ApiError> {
  return useQuery({
    queryKey: queryKeys.boards.calendarFeed(boardId),
    queryFn: () => getBoardCalendarFeed(boardId),
    enabled,
  });
}

export function useGenerateBoardCalendarFeedToken(
  boardId: string,
): UseMutationResult<BoardCalendarFeed, ApiError, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => generateBoardCalendarFeedToken(boardId),
    onSuccess: (feed) => {
      queryClient.setQueryData(queryKeys.boards.calendarFeed(boardId), feed);
    },
  });
}

export function useUpdateBoardCalendarFeed(
  boardId: string,
): UseMutationResult<BoardCalendarFeed, ApiError, BoardCalendarFeedPatch> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => updateBoardCalendarFeed(boardId, body),
    onSuccess: (feed) => {
      queryClient.setQueryData(queryKeys.boards.calendarFeed(boardId), feed);
    },
  });
}

export function useConnectedApps(
  enabled: boolean,
): UseQueryResult<MemberConnectedAppGrantList, ApiError> {
  return useQuery({
    queryKey: queryKeys.meConnectedApps(),
    queryFn: getConnectedApps,
    enabled,
  });
}

export function useCreateConnectedAppGrant(): UseMutationResult<
  ConnectedAppConsentResponse,
  ApiError,
  ConnectedAppConsentBody
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createConnectedAppGrant,
    onSuccess: ({ token: _token, ...grant }) => {
      /*
       * The POST answers the SHIPPED shape, and the list is member-typed, so the
       * prepended row is completed here: a self-minted grant has a live token
       * (`active`), was issued by nobody (`issuedBy: null`), and its hint is
       * left `null` rather than derived from the token on this side — the
       * invalidation below replaces the row with the server's own `tokenHint`.
       */
      const row: MemberConnectedAppGrant = {
        ...grant,
        status: 'active',
        tokenHint: null,
        issuedBy: null,
      };
      queryClient.setQueryData<MemberConnectedAppGrantList>(
        queryKeys.meConnectedApps(),
        (previous) => ({
          connectedApps: [
            row,
            ...(previous?.connectedApps.filter((entry) => entry.id !== row.id) ?? []),
          ],
        }),
      );
      void queryClient.invalidateQueries({ queryKey: queryKeys.meConnectedApps() });
    },
  });
}

export function useRevokeConnectedAppGrant(): UseMutationResult<
  ConnectedAppGrant,
  ApiError,
  { id: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }) => revokeConnectedAppGrant(id),
    onSuccess: (grant) => {
      /*
       * DELETE answers the shipped shape. The member fields are carried over
       * from the cached row (`issuedBy` does not change on a revoke) and the two
       * a revoke does change are set: `status` is `revoked`, and no unrevoked
       * token row is left to give a hint.
       */
      queryClient.setQueryData<MemberConnectedAppGrantList>(
        queryKeys.meConnectedApps(),
        (previous) => {
          const revoked = (row: MemberConnectedAppGrant | undefined) => ({
            ...grant,
            status: 'revoked' as const,
            tokenHint: null,
            issuedBy: row?.issuedBy ?? null,
          });
          return {
            connectedApps: previous?.connectedApps.map((row) =>
              row.id === grant.id ? revoked(row) : row,
            ) ?? [revoked(undefined)],
          };
        },
      );
      void queryClient.invalidateQueries({ queryKey: queryKeys.meChangeRegistrations() });
    },
  });
}

/**
 * AT-8 / AT-13 — reveal or regenerate a token, and hand it to the CALLER only.
 *
 * ⚠ **Deliberately not `useMutation`.** TanStack keeps a mutation's result in
 * `mutation.state.data` inside the `MutationCache` — the token included — for
 * as long as an observer holds it and then for `gcTime` after. AT-13 says the
 * token is never written to the Query cache; a `useMutation` would satisfy the
 * letter of that and still park the cleartext in the client's other cache,
 * where devtools and any `getMutationCache().getAll()` read it. So this is a
 * plain callback: the token travels from the response to the promise the
 * caller awaits, and the only thing the client caches does is mark the list
 * stale so the row's `status` and `tokenHint` are re-read from the server.
 * Busy and error state belong to the caller, which owns the dialog.
 *
 * No `setQueryData` at all, not even of the token-free `connectedApp` half:
 * invalidating is enough, and it leaves no code path here that touches the
 * cache with the response in scope.
 */
export function useRevealConnectedAppToken(): (
  id: string,
) => Promise<ConnectedAppTokenReveal> {
  const queryClient = useQueryClient();
  return useCallback(
    async (id: string) => {
      const revealed = await revealConnectedAppToken(id);
      void queryClient.invalidateQueries({ queryKey: queryKeys.meConnectedApps() });
      return revealed;
    },
    [queryClient],
  );
}
